import type { ReactElement } from 'react';
import { Link } from 'react-router';

type Props = {
  agreedToPrivacy: boolean;
  marketingOptIn: boolean;
  error: string | undefined;
  onAgreedChange: (next: boolean) => void;
  onMarketingChange: (next: boolean) => void;
};

/**
 * 설문 동의 두 개 — [필수] 수집·이용, [선택] 소식 메일.
 *
 * 기존 설문은 체크 하나로 「자료 발송 + 강의 오픈 안내 + 컨설팅 안내」를 한꺼번에 받았다.
 * 강의 오픈 안내는 광고성 정보라 따로, 본인이 골라서 받아야 한다(정보통신망법 제50조).
 * 필수와 한 칸에 묶으면 광고 메일을 받지 않고는 자료도 못 받게 된다.
 *
 * 문구를 바꾸면 api 의 SURVEY_CONSENT_VERSION 을 함께 올린다 — 증빙이 「어느 문구에 동의했나」다.
 */
export function SurveyConsent({
  agreedToPrivacy,
  marketingOptIn,
  error,
  onAgreedChange,
  onMarketingChange,
}: Props): ReactElement {
  return (
    <div
      id="survey-q-agreedToPrivacy"
      className="flex scroll-mt-36 flex-col gap-3 rounded-md border border-border p-4"
    >
      <label htmlFor="survey-consent" className="flex items-start gap-2 text-[13.5px]">
        <input
          id="survey-consent"
          type="checkbox"
          checked={agreedToPrivacy}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'survey-consent-error' : undefined}
          onChange={(event) => onAgreedChange(event.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-primary"
        />
        <span>
          <span className="text-muted-foreground">[필수]</span> 설문 자료 발송을 위한 개인정보
          수집·이용에 동의합니다
        </span>
      </label>
      <dl className="flex flex-col gap-1 rounded-sm bg-secondary px-3 py-2 text-[12px] text-muted-foreground">
        <div className="flex gap-2">
          <dt className="w-[4.5rem] shrink-0">· 수집 항목</dt>
          <dd>이메일, 설문 응답</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-[4.5rem] shrink-0">· 이용 목적</dt>
          <dd>설문 자료 발송, 1:1 무료 컨설팅 추첨 안내</dd>
        </div>
        <div className="flex gap-2">
          <dt className="w-[4.5rem] shrink-0">· 보유 기간</dt>
          <dd>수집일로부터 1년</dd>
        </div>
      </dl>
      <p className="text-[12px] text-muted-foreground">
        동의를 거부하실 수 있으나, 거부하시면 설문에 참여할 수 없습니다.{' '}
        <Link to="/privacy" className="underline underline-offset-2">
          개인정보처리방침
        </Link>
      </p>
      {error ? (
        <p id="survey-consent-error" className="text-[12.5px] text-destructive">
          {error}
        </p>
      ) : null}

      <hr className="border-border" />

      <label htmlFor="survey-marketing" className="flex items-start gap-2 text-[13.5px]">
        <input
          id="survey-marketing"
          type="checkbox"
          checked={marketingOptIn}
          onChange={(event) => onMarketingChange(event.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-primary"
        />
        <span>
          <span className="text-muted-foreground">[선택]</span> 강의 오픈 등 오톱 소식을 메일로
          받겠습니다
        </span>
      </label>
      <p className="text-[12px] text-muted-foreground">
        동의하지 않아도 자료는 그대로 받으실 수 있습니다.{' '}
        <Link to="/marketing-consent" className="underline underline-offset-2">
          광고성 정보 수신 동의 보기
        </Link>
      </p>
    </div>
  );
}
